import { reportError } from '~/utils/reportError';

import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import { STEPS } from './constants';
import type { FlashOfferProperty, SetUpFlashOfferSectionProps } from './types';

export interface UsePropertyCreateHandoffParams {
  onPropertiesRefresh: SetUpFlashOfferSectionProps['onPropertiesRefresh'];
  propertyStep: ReturnType<typeof usePropertyStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  setActiveStep: (value: number | ((prev: number) => number)) => void;
  setErrorMessage: (message: string | null) => void;
  setIsAdvancingStep: (isAdvancing: boolean) => void;
}

/**
 * Owns what happens right after the Property step's own form creates a new property: staging
 * its room drafts, refreshing the parent's property list, and advancing the wizard. Separate
 * from useWizardNavigation because this hand-off is triggered by PropertyForm's own submit
 * (see WizardActions' `submitsCurrentStep`), not by the generic step-advance dispatch.
 */
export function usePropertyCreateHandoff({
  onPropertiesRefresh,
  propertyStep,
  roomsStep,
  setActiveStep,
  setErrorMessage,
  setIsAdvancingStep,
}: UsePropertyCreateHandoffParams) {
  async function refreshProperties(property?: FlashOfferProperty, roomTypes?: Array<App.RoomType>) {
    try {
      await onPropertiesRefresh?.(property, roomTypes);
    } catch (error) {
      reportError(error);
    }
  }

  function handlePropertyCreateStart() {
    setErrorMessage(null);
    propertyStep.handlePropertyCreateStart();
  }

  function handlePropertyCreateError() {
    propertyStep.setIsCreatingNewProperty(false);
    setErrorMessage('Unable to create property.');
  }

  async function handlePropertyCreateSuccess(createdFlashProperty: FlashOfferProperty) {
    if (!createdFlashProperty?.id) {
      handlePropertyCreateError();
      return;
    }

    propertyStep.markPropertyCreated(createdFlashProperty);
    setErrorMessage(null);
    setIsAdvancingStep(true);

    try {
      const { warningMessage } = await roomsStep.prepareRoomDrafts(createdFlashProperty);

      if (warningMessage) {
        setErrorMessage(warningMessage);
      }

      await refreshProperties(createdFlashProperty);
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    } finally {
      setIsAdvancingStep(false);
      propertyStep.setIsCreatingNewProperty(false);
    }
  }

  return {
    handlePropertyCreateError,
    handlePropertyCreateStart,
    handlePropertyCreateSuccess,
  };
}
